import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import MergeTranscriptSpeakers from '@Application/Beneficiaries/Transcripts/Commands/MergeTranscriptSpeakers';
import mergeTranscriptSpeakers from '@Application/Beneficiaries/Transcripts/mergeTranscriptSpeakers';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { setSpeakerAssignmentContext } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/Signals/speakerAssignmentSignal';
import type TranscriptSpeakerView from '@UI/Store/Interviews/Views/TranscriptSpeakerView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, confirmDialog } from '@verclam/ui-kit';

const buttonStyle = css`
	width: 100%;
	justify-content: flex-start;
`;

type Props = {
	readonly facilityId: number;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	/** The speaker open in the panel — always the `:speaker` of the route, so always the merge SOURCE. */
	readonly source: TranscriptSpeakerView;
	readonly target: TranscriptSpeakerView;
};

/**
 * One merge target: the diarization split one person into two labels, and this speaker's turns are
 * folded into `target`'s. Irreversible (§5.6b) — always confirmed, with both names and the turn
 * count spelled out, never a bare "Confirmer ?".
 */
const SpeakerAssignmentMergeChoice: React.FC<Props> = ({
	facilityId,
	subject,
	interviewId,
	source,
	target,
}) => {
	const dispatch = useDispatch();

	const handleAccept = React.useCallback(() => {
		dispatch(
			mergeTranscriptSpeakers(
				new MergeTranscriptSpeakers(facilityId, subject, interviewId, source.id, target.id),
			),
		)
			.then(() => setSpeakerAssignmentContext(null))
			.catch(ignoreHandledError);
	}, [dispatch, facilityId, subject, interviewId, source.id, target.id]);

	const handleClick = React.useCallback(() => {
		confirmDialog({
			header: 'Fusionner deux locuteurs',
			message: (
				<React.Fragment>
					Les {source.turnCount} tours de {source.name} seront attribués à {target.name}, et{' '}
					{source.name} disparaîtra. Cette action est irréversible.
				</React.Fragment>
			),
			acceptLabel: 'Fusionner',
			acceptSeverity: 'danger',
			accept: handleAccept,
		});
	}, [source.turnCount, source.name, target.name, handleAccept]);

	return (
		<Button
			type="button"
			label={`Fusionner avec ${target.name}`}
			severity="secondary"
			outlined
			onClick={handleClick}
			css={buttonStyle}
		/>
	);
};

export default SpeakerAssignmentMergeChoice;
